import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Row, Col } from "antd";
import styles from "../FnaSection.module.css";
import localStyles from "./FnaBasicInfoPlaceholder.module.css";
import {
  basicFnaConfig,
  type FinancialSnapshot,
  type Question,
} from "./fnaBasicConfig";
import { computeSummaries, evaluateConditions } from "./fnaBasicUtils";
import { FnaField } from "./FnaField";

type Props = {
  initialData?: FinancialSnapshot;
  onSave?: (data: FinancialSnapshot) => void;
  onEditingChange?: (editing: boolean) => void;
  mode: "view" | "edit";
};

const emptySnapshot: FinancialSnapshot = {
  income: {},
  expense: {},
  currentAssets: {},
  nonCurrentAssets: {},
  liabilities: {},
  protection: {},
};

export type FnaBasicInfoHandle = {
  save: () => FinancialSnapshot;
  cancel: () => void;
  isDirty: () => boolean;
};

export const FnaBasicInfoPlaceholder = forwardRef<FnaBasicInfoHandle, Props>(function FnaBasicInfoPlaceholder(
  { initialData, onSave, onEditingChange, mode }: Props,
  ref
) {
  const [snapshot, setSnapshot] = useState<FinancialSnapshot>(
    initialData ?? emptySnapshot
  );
  const [draft, setDraft] = useState<FinancialSnapshot>(snapshot);
  const [isDirty, setIsDirty] = useState(false);
  const isEditing = mode === "edit";

  useEffect(() => {
    setSnapshot(initialData ?? emptySnapshot);
    setDraft(initialData ?? emptySnapshot);
    setIsDirty(false);
  }, [initialData]);

  useEffect(() => {
    onEditingChange?.(isEditing && isDirty);
    return () => onEditingChange?.(false);
  }, [isEditing, isDirty, onEditingChange]);

  const computeDirty = (nextDraft: FinancialSnapshot) =>
    JSON.stringify(nextDraft) !== JSON.stringify(snapshot);

  const summaries = useMemo(() => computeSummaries(draft), [draft]);

  const handleValueChange = (
    question: Question,
    value: number | string | boolean | null
  ) => {
    setDraft((prev) => {
      const next: FinancialSnapshot = {
        ...prev,
        [question.section]: {
          ...(prev[question.section] as Record<string, any>),
          [question.key]: value === null ? undefined : value,
        },
      };
      setIsDirty(computeDirty(next));
      return next;
    });
  };

  const handleSave = useCallback(() => {
    setSnapshot(draft);
    onSave?.(draft);
    setIsDirty(false);
    return draft;
  }, [draft, onSave]);

  const handleCancel = useCallback(() => {
    setDraft(snapshot);
    setIsDirty(false);
  }, [snapshot]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
      isDirty: () => isDirty,
    }),
    [handleSave, handleCancel, isDirty]
  );

  const renderControl = (question: Question) => {
    const value = (draft[question.section] as any)?.[question.key];
    const visible = evaluateConditions(question.visibleWhen, draft);
    if (!visible) return null;

    const isInlineSwitch =
      question.kind === "switch" && question.variant === "inlineSwitch";

    if (isInlineSwitch) {
      return (
        <Col
          span={question.colSpan ?? 24}
          key={`${question.section}-${question.key}`}
        >
          <FnaField
            question={question}
            value={value}
            mode={mode}
            onChange={(val) => handleValueChange(question, val as any)}
          />
        </Col>
      );
    }

    return (
      <Col
        span={question.colSpan ?? 12}
        className={localStyles.fieldRow}
        key={`${question.section}-${question.key}`}
      >
        <div className={localStyles.fieldLabel}>{question.label}</div>
        <FnaField
          question={question}
          value={value}
          mode={mode}
          onChange={(val) => handleValueChange(question, val as any)}
        />
      </Col>
    );
  };

  return (
    <div className={`${styles.fnaCard} ${localStyles.container}`}>
      {basicFnaConfig.map((section, sectionIdx) => (
        <div key={section.title}>
          <div className={localStyles.sectionTitle}>
            {section.title}
          </div>

          {section.blocks.map((block, blockIdx) => (
            <div key={block.key}>
              {block.headerSummaryKey && (
                <div className={localStyles.headerBox}>
                  <span>{block.headerLabel}</span>
                  <strong>
                    {block.headerPrefix ?? ""}
                    {summaries[block.headerSummaryKey].toLocaleString()}
                    {block.headerValueSuffix ?? ""}
                  </strong>
                </div>
              )}

              {block.groups.map((group, groupIdx) => (
                <React.Fragment key={group.key}>
                  <div className={localStyles.sectionBlock}>
                    <div className={localStyles.subSectionHeader}>
                      <span className={localStyles.subSectionTitle}>
                        {group.title}
                      </span>
                      {group.summaryKey && (
                        <span className={localStyles.subSectionSummary}>
                          {group.summaryLabel ?? block.summaryLabelPrefix ?? ""}
                          {summaries[group.summaryKey].toLocaleString()}
                          {group.summaryValueSuffix ?? ""}
                        </span>
                      )}
                    </div>
                    <Row gutter={group.gutter ?? [16, 24]}>
                      {group.questions.map((q) => renderControl(q))}
                    </Row>
                  </div>
                  {groupIdx !== block.groups.length - 1 && (
                    <div
                      className={`${localStyles.separator} ${localStyles.separatorWide}`}
                    />
                  )}
                </React.Fragment>
              ))}

              {blockIdx !== section.blocks.length - 1 && (
                <div
                  className={`${localStyles.separator} ${localStyles.separatorWide}`}
                />
              )}
            </div>
          ))}

          {sectionIdx !== basicFnaConfig.length - 1 && (
            <div
              className={`${localStyles.separator} ${localStyles.separatorSection}`}
            />
          )}
        </div>
      ))}
    </div>
  );
});
